import { useCallback, useEffect, useRef, useState } from 'react';

import Arrow from './Arrow';
import AutoClose from './AutoClose';
import CloseIcon from './closeIcon';
import Dissolve from './Dissolve';
import { getTooltipPosition, Position } from './utils';

import {
  CloseContainer,
  ContentContainer,
  StyledTooltipOverlay,
  TitleContainer,
} from './styles';

interface OverlayProps {
  title: string;
  description: React.ReactNode;
  visible: boolean;
  position: Position;
  onClose: () => void;
  wrapperRef: Element | null;
}

export const Overlay = ({
  title,
  description,
  visible,
  onClose,
  position,
  wrapperRef,
}: OverlayProps) => {
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const toolTipRef = useRef(null);

  const measuredRef = useCallback(
    (ref) => {
      if (!ref || !wrapperRef) return;
      toolTipRef.current = ref;
      setCoords(getTooltipPosition(position, ref, wrapperRef));
    },
    [wrapperRef],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleClick = (e: any) => {
    if (!toolTipRef.current || !wrapperRef) return;
    if (
      !(toolTipRef.current as Element).contains(e.target) &&
      !wrapperRef.contains(e.target)
    ) {
      // cliose if user cliked outside of child and tooltip both
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  });

  return (
    <Dissolve visible={visible}>
      <AutoClose
        tooltip={toolTipRef.current}
        wrapper={wrapperRef}
        close={onClose}
      >
        <StyledTooltipOverlay
          style={{
            top: coords.top,
            left: coords.left,
          }}
          ref={measuredRef}
          data-testid="workspace-popup-overlay"
        >
          <TitleContainer>
            {title}
            <CloseContainer
              onClick={onClose}
              data-testid="workspace-popup-close"
            >
              <CloseIcon />
            </CloseContainer>
          </TitleContainer>
          <ContentContainer>{description}</ContentContainer>
          <Arrow position={position} />
        </StyledTooltipOverlay>
      </AutoClose>
    </Dissolve>
  );
};

export default Overlay;
